// MF-67 — Entries over time per type (weekly or monthly, as the stats endpoint bucketed). One thin
// line per type present (tenant label + colour, configured order), a soft area, end labels naming
// each series (direct labels), and an axis crosshair tooltip. One y-axis (counts).
import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import {
  FONT,
  INK,
  baseTextStyle,
  legendBase,
  tooltipBase,
  fmtNumber,
  orderedTypes,
  typeStyle
} from './chartTheme'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
// period is a calendar date 'YYYY-MM-DD' in the school's zone — format it without re-zoning.
const periodLabel = (p, bucket) => {
  const [y, m, d] = String(p).split('-').map(Number)
  return bucket === 'week' ? `${d} ${MONTHS[m - 1]}` : `${MONTHS[m - 1]} ${String(y).slice(2)}`
}

const TrendChart = ({
  trend = [],
  bucket = 'month',
  types = { list: [], byCode: new Map() },
  height = 300
}) => {
  const option = useMemo(() => {
    const periods = [...new Set(trend.map((r) => r.period))].sort()
    const value = (period, code) => trend.find((r) => r.period === period && r.type === code)?.count || 0
    // Only series with data under the current filters (no flat line at zero).
    const codes = orderedTypes([...new Set(trend.filter((r) => r.count > 0).map((r) => r.type))], types.list)
    const styles = codes.map((c) => ({ code: c, ...typeStyle(c, types.byCode) }))
    return {
      textStyle: baseTextStyle,
      animationDuration: 400,
      legend: { ...legendBase, data: styles.map((s) => s.label) },
      grid: { top: 36, left: 8, right: 84, bottom: 4, containLabel: true },
      tooltip: {
        ...tooltipBase,
        trigger: 'axis',
        axisPointer: { type: 'line', lineStyle: { color: INK.muted, width: 1 } },
        valueFormatter: (v) => fmtNumber(v)
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: periods.map((p) => periodLabel(p, bucket)),
        axisLine: { lineStyle: { color: INK.axis } },
        axisTick: { show: false },
        axisLabel: { color: INK.secondary, fontFamily: FONT, fontSize: 11, hideOverlap: true }
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        splitLine: { lineStyle: { color: INK.grid } },
        axisLabel: { color: INK.secondary, fontFamily: FONT, fontSize: 11 }
      },
      series: styles.map((s) => ({
        name: s.label,
        type: 'line',
        data: periods.map((p) => value(p, s.code)),
        smooth: 0.2,
        symbol: 'circle',
        symbolSize: 8,
        showSymbol: periods.length <= 16,
        lineStyle: { width: 2, color: s.color },
        itemStyle: { color: s.color, borderColor: INK.surface, borderWidth: 2 },
        areaStyle: { color: s.color, opacity: 0.08 },
        emphasis: { focus: 'series' },
        endLabel: {
          show: true,
          formatter: ({ seriesName }) => seriesName,
          color: INK.primary,
          fontFamily: FONT,
          fontSize: 11,
          distance: 6
        }
      }))
    }
  }, [trend, bucket, types])

  return (
    <ReactECharts option={option} notMerge style={{ height, width: '100%' }} opts={{ renderer: 'svg' }} />
  )
}

export default TrendChart
